import { createFileRoute, Link } from "@tanstack/react-router";
import * as React from "react";
import { CheckCircle2, Mail, MessageCircle, Package, RotateCcw } from "lucide-react";
import { SiteShell } from "@/components/SiteShell";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/contexts/LocaleContext";
import { DICTS } from "@/lib/i18n";
import { LECMO_WA_NUMBER } from "@/lib/agentConfig";
import { ThankYouVideoPlayer } from "@/components/order/ThankYouVideoPlayer";

const LAST_CHECKOUT_HANDLES_KEY = "lecmo:last_checkout_handles";

const SUPPORT_EMAIL = "support@lecmo.org";

export const Route = createFileRoute("/thank-you")({
  validateSearch: (s: Record<string, unknown>) => ({
    order: typeof s.order === "string" ? s.order : undefined,
    order_id: typeof s.order_id === "string" ? s.order_id : undefined,
    // ⚠️ لا يُستخدم في التحويلات: قيم الرابط غير موثوقة، والعرض فقط.
    is_gift: s.is_gift === "true" || s.is_gift === true,
    /** توكن تتبّع موقَّع (من الإيميل/الواتساب) — إثبات ملكيّة الطلب */
    t: typeof s.t === "string" ? s.t : undefined,
  }),

  head: () => ({
    meta: [
      { title: DICTS.en["thankyou.meta.title"] },
      { name: "description", content: DICTS.en["thankyou.meta.description"] },
      { name: "robots", content: "noindex" },
      { property: "og:title", content: DICTS.en["thankyou.meta.title"] },
      { property: "og:description", content: DICTS.en["thankyou.meta.description"] },
    ],
  }),
  component: ThankYouPage,
});

function ThankYouPage() {
  const { t, locale } = useLocale();
  const search = Route.useSearch() as {
    order?: string;
    order_id?: string;
    is_gift?: boolean;
    t?: string;
  };
  const orderRef = search.order ?? search.order_id;
  const supportLine = t("thankyou.support").replace("{email}", SUPPORT_EMAIL);

  const [handles, setHandles] = React.useState<string[]>([]);
  React.useEffect(() => {
    try {
      const raw = localStorage.getItem(LAST_CHECKOUT_HANDLES_KEY);
      const parsed = raw ? (JSON.parse(raw) as unknown) : null;
      if (Array.isArray(parsed)) {
        setHandles(parsed.filter((h): h is string => typeof h === "string"));
      }
    } catch {
      // ignore
    }
  }, []);

  // ⛔️ لا تحويل شراء من هذه الصفحة إطلاقاً (لا Google Ads · لا GA4 · لا OpenAI).
  // العميل لا يصل إليها دائماً، والمصدر الوحيد لحدث الشراء هو تطبيق
  // Google & YouTube في Shopify. الصفحة عرضيّة فقط، وروابط البريد/واتساب
  // وفيديو الشكر تعتمد عليها فتبقى كما هي.
  // محروس باختبار `src/__tests__/purchase-single-source.test.ts`.



  const waUrl = orderRef
    ? `https://wa.me/${LECMO_WA_NUMBER}?text=${encodeURIComponent(
        locale === "ar"
          ? `مرحباً، لديّ استفسار بخصوص الطلب ${orderRef}`
          : `Hi, I have a question about order ${orderRef}`,
      )}`
    : `https://wa.me/${LECMO_WA_NUMBER}`;

  return (
    <SiteShell>
      <section className="mx-auto w-full max-w-[640px] px-4 py-16 text-center md:py-24">
        <div className="mx-auto mb-6 inline-flex h-16 w-16 items-center justify-center rounded-full bg-accent/10 text-accent">
          <CheckCircle2 className="h-8 w-8" />
        </div>
        <p className="t-eyebrow tracking-[var(--tracking-wider)] text-muted-foreground">
          {t("thankyou.eyebrow")}
        </p>
        <h1 className="mt-3 t-display">{t("thankyou.title")}</h1>
        <p className="mx-auto mt-4 max-w-[480px] text-muted-foreground">
          {t("thankyou.subtitle")}
        </p>

        {orderRef && (
          <div className="mt-8 inline-flex items-center gap-2 rounded-none border border-border bg-card px-4 py-2 text-sm">
            <Package className="h-4 w-4 text-accent" />
            <span className="text-muted-foreground">{t("thankyou.order_number")}:</span>
            <span className="font-mono font-semibold">{orderRef}</span>
          </div>
        )}

        <ThankYouVideoPlayer handles={handles} className="mx-auto mt-10 max-w-[520px]" />

        <div className="mx-auto mt-10 max-w-[480px] rounded-none border border-border bg-card p-6 text-start">
          <h2 className="mb-4 text-base font-semibold">{t("thankyou.next_title")}</h2>
          <ul className="space-y-3 text-sm text-muted-foreground">
            <li className="flex items-start gap-3">
              <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
              <span>{t("thankyou.next_email")}</span>
            </li>
            <li className="flex items-start gap-3">
              <MessageCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
              <span>{t("thankyou.next_whatsapp")}</span>
            </li>
            <li className="flex items-start gap-3">
              <Package className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
              <span>{t("thankyou.next_track")}</span>
            </li>
          </ul>
        </div>

        <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
          <Button asChild>
            <Link to="/account/orders">{t("thankyou.cta_track")}</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/shop">{t("thankyou.cta_shop")}</Link>
          </Button>
          {/* زرّ الهدايا معطّل مؤقّتاً */}
          <Button asChild variant="ghost">
            <a href={waUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2">
              <MessageCircle className="h-4 w-4" />
              {t("thankyou.cta_whatsapp")}
            </a>
          </Button>
        </div>

        <div className="mx-auto mt-12 max-w-[480px] border border-border bg-card p-6 text-start">
          <h2 className="text-base font-semibold">{t("thankyou.help_title")}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t("thankyou.help_subtitle")}</p>

          <div className="mt-5 divide-y divide-border">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex min-h-[56px] items-start gap-3 py-3"
            >
              <MessageCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
              <span>
                <span className="block text-sm font-medium">{t("thankyou.help_whatsapp_title")}</span>
                <span className="block text-xs text-muted-foreground">
                  {t("thankyou.help_whatsapp_desc")}
                </span>
              </span>
            </a>

            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex min-h-[56px] items-start gap-3 py-3"
            >
              <Mail className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
              <span>
                <span className="block text-sm font-medium">{t("thankyou.help_email_title")}</span>
                <span className="block text-xs text-muted-foreground">
                  {t("thankyou.help_email_desc").replace("{email}", SUPPORT_EMAIL)}
                </span>
              </span>
            </a>

            <Link to="/track-order" className="flex min-h-[56px] items-start gap-3 py-3">
              <Package className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
              <span>
                <span className="block text-sm font-medium">{t("thankyou.help_track_title")}</span>
                <span className="block text-xs text-muted-foreground">
                  {t("thankyou.help_track_desc")}
                </span>
              </span>
            </Link>

            <Link to="/returns" className="flex min-h-[56px] items-start gap-3 py-3">
              <RotateCcw className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />
              <span>
                <span className="block text-sm font-medium">{t("thankyou.help_returns_title")}</span>
                <span className="block text-xs text-muted-foreground">
                  {t("thankyou.help_returns_desc")}
                </span>
              </span>
            </Link>
          </div>
        </div>

        <p className="mt-10 text-xs text-muted-foreground">{supportLine}</p>
      </section>

    </SiteShell>
  );
}
